/**
 * Analytics Service
 * Handles user analytics, feature usage tracking, and engagement metrics in production
 */

import { productionConfig, isProduction } from '../config/production';

export interface AnalyticsEvent {
  name: string;
  category: 'user_action' | 'navigation' | 'feature_usage' | 'performance' | 'error' | 'engagement';
  properties?: Record<string, any>;
  timestamp: string;
  sessionId: string;
  userId?: string;
  url: string;
  userAgent: string;
}

export interface UserProperties {
  userId?: string;
  email?: string;
  membershipTier?: string;
  registrationDate?: string;
  lastLoginDate?: string;
  totalReadings?: number;
  favoriteSpread?: string;
  preferredTheme?: string;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
  browserName?: string;
  operatingSystem?: string;
}

export interface PageView {
  url: string;
  title: string;
  referrer: string;
  timestamp: string;
  sessionId: string;
  userId?: string;
  loadTime?: number;
  userAgent: string;
}

class AnalyticsService {
  private sessionId: string;
  private isInitialized = false;
  private eventQueue: AnalyticsEvent[] = [];
  private pageViewQueue: PageView[] = [];
  private userProperties: UserProperties = {};
  private sessionStartTime: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
  }

  /**
   * Initialize analytics service
   */
  public initialize(): void {
    if (this.isInitialized || !productionConfig.analytics.enabled) {
      return;
    }

    this.detectUserProperties();
    this.setupPageViewTracking();
    this.setupUserInteractionTracking();
    this.startPeriodicReporting();
    this.trackSessionStart();
    
    this.isInitialized = true;
    console.log('Analytics service initialized');
  }

  /**
   * Track a custom event
   */
  public trackEvent(
    name: string,
    category: AnalyticsEvent['category'],
    properties?: Record<string, any>
  ): void {
    if (!productionConfig.analytics.enabled) {
      return;
    }

    // Apply sampling rate
    if (Math.random() > productionConfig.analytics.sampleRate) {
      return;
    }

    const event: AnalyticsEvent = {
      name,
      category,
      properties,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userProperties.userId,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.queueEvent(event);
  }

  /**
   * Track page view
   */
  public trackPageView(title?: string): void {
    if (!productionConfig.analytics.enabled) {
      return;
    }

    const pageView: PageView = {
      url: window.location.href,
      title: title || document.title,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userProperties.userId,
      userAgent: navigator.userAgent,
    };

    this.queuePageView(pageView);
  }

  /**
   * Set user properties
   */
  public setUserProperties(properties: Partial<UserProperties>): void {
    this.userProperties = { ...this.userProperties, ...properties };
    
    // Track user property update
    this.trackEvent('user_properties_updated', 'user_action', {
      updatedProperties: Object.keys(properties),
    });
  }

  /**
   * Set user ID
   */
  public setUserId(userId: string): void {
    this.userProperties.userId = userId;
    
    // Track user identification
    this.trackEvent('user_identified', 'user_action', {
      userId,
    });
  }

  /**
   * Clear user data
   */
  public clearUserData(): void {
    const previousUserId = this.userProperties.userId;
    this.userProperties = {};
    
    // Track user logout
    this.trackEvent('user_logout', 'user_action', {
      previousUserId,
    });
  }

  /**
   * Track feature usage
   */
  public trackFeatureUsage(featureName: string, action: string, properties?: Record<string, any>): void {
    this.trackEvent(`feature_${action}`, 'feature_usage', {
      featureName,
      action,
      ...properties,
    });
  }

  /**
   * Track user engagement
   */
  public trackEngagement(type: 'scroll' | 'click' | 'focus' | 'time_spent', properties?: Record<string, any>): void {
    this.trackEvent(`engagement_${type}`, 'engagement', properties);
  }

  /**
   * Track performance metrics
   */
  public trackPerformance(metric: string, value: number, properties?: Record<string, any>): void {
    if (!productionConfig.analytics.trackPerformanceMetrics) {
      return;
    }

    this.trackEvent('performance_metric', 'performance', {
      metric,
      value,
      ...properties,
    });
  }

  /**
   * Track error events
   */
  public trackError(error: string, properties?: Record<string, any>): void {
    this.trackEvent('error_occurred', 'error', {
      error,
      ...properties,
    });
  }

  /**
   * Track conversion events
   */
  public trackConversion(type: 'registration' | 'purchase' | 'subscription' | 'reading_completed', properties?: Record<string, any>): void {
    this.trackEvent(`conversion_${type}`, 'user_action', {
      conversionType: type,
      ...properties,
    });
  }

  /**
   * Track PWA installation
   */
  public trackPWAInstallation(properties?: Record<string, any>): void {
    this.trackEvent('pwa_installed', 'engagement', {
      installationMethod: 'browser_prompt',
      ...properties,
    });
  }

  /**
   * Track PWA installation prompt shown
   */
  public trackPWAInstallPromptShown(properties?: Record<string, any>): void {
    this.trackEvent('pwa_install_prompt_shown', 'engagement', properties);
  }

  /**
   * Track PWA installation prompt dismissed
   */
  public trackPWAInstallPromptDismissed(properties?: Record<string, any>): void {
    this.trackEvent('pwa_install_prompt_dismissed', 'engagement', properties);
  }

  /**
   * Track PWA launch
   */
  public trackPWALaunch(displayMode: 'standalone' | 'browser' | 'minimal-ui' | 'fullscreen', properties?: Record<string, any>): void {
    this.trackEvent('pwa_launched', 'engagement', {
      displayMode,
      isStandalone: displayMode === 'standalone',
      ...properties,
    });
  }

  /**
   * Track offline usage
   */
  public trackOfflineUsage(action: string, properties?: Record<string, any>): void {
    this.trackEvent('offline_usage', 'engagement', {
      action,
      isOnline: navigator.onLine,
      ...properties,
    });
  }

  /**
   * Track service worker events
   */
  public trackServiceWorkerEvent(event: 'installed' | 'activated' | 'updated' | 'error', properties?: Record<string, any>): void {
    this.trackEvent(`service_worker_${event}`, 'engagement', properties);
  }

  /**
   * Track reading creation
   */
  public trackReadingCreated(spreadType: string, properties?: Record<string, any>): void {
    this.trackFeatureUsage('tarot_reading', 'created', {
      spreadType,
      ...properties,
    });
  }

  /**
   * Track shop purchase
   */
  public trackShopPurchase(itemType: string, amount: number, properties?: Record<string, any>): void {
    this.trackFeatureUsage('shop', 'purchase', {
      itemType,
      amount,
      ...properties,
    });
  }

  /**
   * Track search usage
   */
  public trackSearch(query: string, resultsCount: number, properties?: Record<string, any>): void {
    this.trackFeatureUsage('search', 'performed', {
      queryLength: query.length,
      resultsCount,
      ...properties,
    });
  }

  /**
   * Track filter usage
   */
  public trackFilterUsage(filterType: string, filterValue: any, properties?: Record<string, any>): void {
    this.trackFeatureUsage('filter', 'applied', {
      filterType,
      filterValue,
      ...properties,
    });
  }

  /**
   * Get session analytics
   */
  public getSessionAnalytics(): {
    sessionId: string;
    duration: number;
    pageViews: number;
    events: number;
    userProperties: UserProperties;
  } {
    return {
      sessionId: this.sessionId,
      duration: Date.now() - this.sessionStartTime,
      pageViews: this.pageViewQueue.length,
      events: this.eventQueue.length,
      userProperties: this.userProperties,
    };
  }

  /**
   * Detect user properties from browser/device
   */
  private detectUserProperties(): void {
    const userAgent = navigator.userAgent;
    
    // Detect device type
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = /iPad|Android(?!.*Mobile)/i.test(userAgent) ? 'tablet' : 'mobile';
    }

    // Detect browser
    let browserName = 'Unknown';
    if (userAgent.includes('Chrome')) browserName = 'Chrome';
    else if (userAgent.includes('Firefox')) browserName = 'Firefox';
    else if (userAgent.includes('Safari')) browserName = 'Safari';
    else if (userAgent.includes('Edge')) browserName = 'Edge';

    // Detect OS
    let operatingSystem = 'Unknown';
    if (userAgent.includes('Windows')) operatingSystem = 'Windows';
    else if (userAgent.includes('Mac')) operatingSystem = 'macOS';
    else if (userAgent.includes('Linux')) operatingSystem = 'Linux';
    else if (userAgent.includes('Android')) operatingSystem = 'Android';
    else if (userAgent.includes('iOS')) operatingSystem = 'iOS';

    this.userProperties = {
      ...this.userProperties,
      deviceType,
      browserName,
      operatingSystem,
    };
  }

  /**
   * Setup automatic page view tracking
   */
  private setupPageViewTracking(): void {
    // Track initial page view
    this.trackPageView();

    // Track page views on navigation (for SPAs)
    let currentUrl = window.location.href;
    const observer = new MutationObserver(() => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        this.trackPageView();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Track page view on popstate (back/forward navigation)
    window.addEventListener('popstate', () => {
      this.trackPageView();
    });
  }

  /**
   * Setup automatic user interaction tracking
   */
  private setupUserInteractionTracking(): void {
    if (!productionConfig.analytics.trackUserInteractions) {
      return;
    }

    // Track clicks on important elements
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      
      // Track button clicks
      if (tagName === 'button' || target.closest('button')) {
        const button = tagName === 'button' ? target : target.closest('button');
        this.trackEvent('button_click', 'user_action', {
          buttonText: button?.textContent?.trim(),
          buttonId: button?.id,
          buttonClass: button?.className,
        });
      }
      
      // Track link clicks
      if (tagName === 'a' || target.closest('a')) {
        const link = tagName === 'a' ? target : target.closest('a');
        this.trackEvent('link_click', 'user_action', {
          linkText: link?.textContent?.trim(),
          linkHref: (link as HTMLAnchorElement)?.href,
          linkId: link?.id,
        });
      }
    });

    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollDepth > maxScrollDepth && scrollDepth % 25 === 0) {
        maxScrollDepth = scrollDepth;
        this.trackEngagement('scroll', { depth: scrollDepth });
      }
    });

    // Track time spent on page
    let pageStartTime = Date.now();
    window.addEventListener('beforeunload', () => {
      const timeSpent = Date.now() - pageStartTime;
      this.trackEngagement('time_spent', { 
        duration: timeSpent,
        url: window.location.href,
      });
    });

    // Track focus/blur for engagement
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEngagement('focus', { type: 'blur' });
      } else {
        this.trackEngagement('focus', { type: 'focus' });
      }
    });
  }

  /**
   * Track session start
   */
  private trackSessionStart(): void {
    this.trackEvent('session_start', 'user_action', {
      sessionId: this.sessionId,
      userProperties: this.userProperties,
    });
  }

  /**
   * Queue event for batch reporting
   */
  private queueEvent(event: AnalyticsEvent): void {
    this.eventQueue.push(event);

    // Log to console in development
    if (!isProduction()) {
      console.log('Analytics event:', event);
    }
  }

  /**
   * Queue page view for batch reporting
   */
  private queuePageView(pageView: PageView): void {
    this.pageViewQueue.push(pageView);

    // Log to console in development
    if (!isProduction()) {
      console.log('Page view:', pageView);
    }
  }

  /**
   * Send events to analytics service
   */
  private async sendEvents(events: AnalyticsEvent[]): Promise<void> {
    if (events.length === 0) {
      return;
    }

    try {
      if (isProduction()) {
        await fetch('/api/analytics/events', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ events }),
        });
      } else {
        // Store in local storage for development
        const existingEvents = JSON.parse(localStorage.getItem('analytics_events') || '[]');
        localStorage.setItem('analytics_events', JSON.stringify([...existingEvents, ...events]));
      }
    } catch (error) {
      console.warn('Failed to send analytics events:', error);
    }
  }

  /**
   * Send page views to analytics service
   */
  private async sendPageViews(pageViews: PageView[]): Promise<void> {
    if (pageViews.length === 0) {
      return;
    }

    try {
      if (isProduction()) {
        await fetch('/api/analytics/pageviews', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pageViews }),
        });
      } else {
        // Store in local storage for development
        const existingPageViews = JSON.parse(localStorage.getItem('analytics_pageviews') || '[]');
        localStorage.setItem('analytics_pageviews', JSON.stringify([...existingPageViews, ...pageViews]));
      }
    } catch (error) {
      console.warn('Failed to send page views:', error);
    }
  }

  /**
   * Start periodic reporting of queued items
   */
  private startPeriodicReporting(): void {
    // Send queued items every 60 seconds
    setInterval(() => {
      if (this.eventQueue.length > 0) {
        const events = [...this.eventQueue];
        this.eventQueue = [];
        this.sendEvents(events);
      }

      if (this.pageViewQueue.length > 0) {
        const pageViews = [...this.pageViewQueue];
        this.pageViewQueue = [];
        this.sendPageViews(pageViews);
      }
    }, 60000);

    // Send queued items before page unload
    window.addEventListener('beforeunload', () => {
      // Track session end
      this.trackEvent('session_end', 'user_action', {
        sessionDuration: Date.now() - this.sessionStartTime,
      });

      // Send remaining data
      if (this.eventQueue.length > 0) {
        navigator.sendBeacon('/api/analytics/events', JSON.stringify({ events: this.eventQueue }));
      }
      if (this.pageViewQueue.length > 0) {
        navigator.sendBeacon('/api/analytics/pageviews', JSON.stringify({ pageViews: this.pageViewQueue }));
      }
    });
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();